import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import uuid from 'uuid-random';
import { FlexBehavior, FlexJustify, Spinner } from 'react-magma-dom';
import { Exemplar } from '../../../components/Exemplar';
import { StyledCenteredSpinnerWrapper } from '../../../components/Styled';
import { ContactUsError } from '../../../components/ContactUsError';
import { ComingSoon } from '../../../components/ComingSoon';
import { collectionsApi } from '../../../API/collections';
import { ExemplarType } from '../../../types';
import { EXEMPLAR_STAGES, NOT_FOUND } from '../../../constants';
import { ExemplarStageAccessCode } from '../ExemplarStageAccessCode';
import { getStageAvailability } from '../../../utils/envs';
import { getStageColors } from '../../../utils';
import AuthContext from '../../../contexts/authContext';

interface IExemplarsStage extends RouteComponentProps {
  stage: string;
}

export const ExemplarsStage: React.FC<IExemplarsStage> = (props): JSX.Element => {
  const { stage, navigate } = props;

  const [isLoading, setLoading] = useState(true);
  const [exemplars, setExemplars] = useState<ExemplarType[]>([]);
  const [isError, setError] = useState(false);
  const [AccessCodeA, setAccessCodeA] = useState(false);
  const [AccessCodeB, setAccessCodeB] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);

  const { setDefaultStage } = useContext(AuthContext);

  const clearState = () => {
    setLoading(true);
    setExemplars([]);
    setError(false);
    setIsComingSoon(false);
  };

  useEffect(() => {
    if (!(stage in EXEMPLAR_STAGES)) {
      navigate && navigate(`/${NOT_FOUND}`);
    }

    setDefaultStage(stage);

    if (!getStageAvailability(stage)) {
      setLoading(false);
      setIsComingSoon(true);

      return clearState;
    }

    let isUnmounted = false;

    (async () => {
      try {
        const response = await collectionsApi.getAll(EXEMPLAR_STAGES[stage]);
        const hasA = response.data.data.some(data => data.title === `${EXEMPLAR_STAGES[stage]} A`);
        const hasB = response.data.data.some(data => data.title === `${EXEMPLAR_STAGES[stage]} B`);
        setAccessCodeA(!hasA);
        setAccessCodeB(!hasB);

        !isUnmounted && setExemplars(response.data.data);
      } catch (error) {
        !isUnmounted && setError(true);
      } finally {
        !isUnmounted && setLoading(false);
      }
    })();

    return () => {
      isUnmounted = true;
      clearState();
    };
  }, [stage]);

  const renderExemplars = (exemplars: ExemplarType[]): JSX.Element[] => {
    return exemplars.map((exemplar: ExemplarType) => {
      return <Exemplar exemplar={exemplar} key={uuid()} data-testid="exemplar" />;
    });
  };

  const renderPage = () => {
    if (isLoading) {
      const { main } = getStageColors(stage);

      return (
        <StyledCenteredSpinnerWrapper behavior={FlexBehavior.container} justify={FlexJustify.center} testId="spinner">
          <Spinner color={main} size={50} />
        </StyledCenteredSpinnerWrapper>
      );
    } else if (isError) {
      return <ContactUsError />;
    } else if (isComingSoon) {
      return <ComingSoon stageNotReady />;
    } else {
      return (
        <div>
          {renderExemplars(exemplars)}
          {AccessCodeA ? <ExemplarStageAccessCode stage={EXEMPLAR_STAGES[stage]} level="A" /> : ''}
          {AccessCodeB ? <ExemplarStageAccessCode stage={EXEMPLAR_STAGES[stage]} level="B" /> : ''}
        </div>
      );
    }
  };

  return (
    <>
      {props.children}
      {renderPage()}
    </>
  );
};
